import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  CssBaseline,
  Divider,
  Grid,
  Paper,
  Radio,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  styled,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import { axiosClient } from "../AxiosClient";
import { PRAMI_COLORS, pramiCertificadosTheme } from "../expedicionCertificados/certificadosTheme";
import CondonarQuotaDialog from "./CondonarQuotaDialog";
import CondonarMoraGacDialog from "./CondonarMoraGacDialog";
import CreditOffersDialog from "./CreditOffersDialog";
import OfertaDialog from "./OfertaDialog";
import OfertaCreditoDialog from "./OfertaCreditoDialog";
import OfertasListDialog from "./OfertasListDialog";
import {
  CREDIT_STATUS_LABELS,
  QUOTA_STATUS_LABELS,
  formatCreditOldId,
  formatDate,
  formatMoney,
  isQuotaOverdue,
  roundMoney,
  squareFieldSx,
} from "./condonationFormat";

// Una cuota (activeQuota) es "vencida" (condonable) si su `quota` está PENDING con payDate <= hoy.
const isActiveQuotaOverdue = (activeQuota) => isQuotaOverdue(activeQuota?.quota);

// El checkbox de GAC/mora solo se habilita para cuotas vencidas con pendiente de ese componente > 0
// (es decir, con GAC generado o interés moratorio por condonar). Solo se puede condonar el 100%.
const pendingGacOf = (activeQuota) => Number(activeQuota?.pending?.pendingGac || 0);
const pendingMoraOf = (activeQuota) => Number(activeQuota?.pending?.pendingMora || 0);
const canCondoneGac = (activeQuota) => isActiveQuotaOverdue(activeQuota) && pendingGacOf(activeQuota) > 0;
const canCondoneMora = (activeQuota) => isActiveQuotaOverdue(activeQuota) && pendingMoraOf(activeQuota) > 0;

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(2),
  textAlign: "left",
  color: theme.palette.text.secondary,
  border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
  boxShadow: "0 1px 3px rgba(22, 30, 85, 0.06)",
}));

const ButtonPrincipal = styled(Button)(({ theme }) => ({
  flexGrow: 0,
  borderRadius: "20px",
  gap: "10px",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  "&:hover": {
    backgroundColor: PRAMI_COLORS.hoverMint,
    color: PRAMI_COLORS.drawerNavy,
  },
}));

const ButtonSecondary = styled(Button)({
  borderRadius: "20px",
  color: PRAMI_COLORS.primary,
  borderColor: PRAMI_COLORS.primary,
  textTransform: "none",
});

const SectionTitle = styled(Typography)({
  color: PRAMI_COLORS.primary,
  fontWeight: 600,
});

const surfacePaperSx = {
  bgcolor: "#fff",
  border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
  borderRadius: 2,
  boxShadow: "0 1px 3px rgba(22, 30, 85, 0.06)",
};

const headerCellSx = {
  color: PRAMI_COLORS.primaryContrast,
  fontWeight: 600,
};

const selectedRowSx = {
  cursor: "pointer",
  "&.Mui-selected": { bgcolor: PRAMI_COLORS.rowHighlight },
  "&.Mui-selected:hover": { bgcolor: PRAMI_COLORS.rowHighlight },
};

const statusChip = (status, labels) => {
  const map = {
    COMPLETED: "success",
    PAID: "success",
    CONDONED: "success",
    CONDONADA: "success",
    APPROVED: "primary",
    PENDING: "default",
    PARTIALLY_PAID: "warning",
    OVERDUE: "error",
    IN_ARREARS: "error",
    CANCELLED: "default",
  };
  const color = map[status];
  const label = labels[status] || status || "-";
  if (color && color !== "default") {
    return <Chip label={label} size="small" color={color} sx={{ fontWeight: 600 }} />;
  }
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{ borderColor: PRAMI_COLORS.surfaceBorder, fontWeight: 600 }}
    />
  );
};

export default function Condonaciones() {
  const [textClient, setTextClient] = useState("");
  const [dataClient, setDataClient] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchedClient, setSearchedClient] = useState(null);

  const [credits, setCredits] = useState(null);
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [quotas, setQuotas] = useState(null);

  const [condonarTarget, setCondonarTarget] = useState(null);
  const [ofertaTarget, setOfertaTarget] = useState(null);
  const [ofertaCreditoTarget, setOfertaCreditoTarget] = useState(null);
  const [moraGacTarget, setMoraGacTarget] = useState(null);
  const [ofertasListTarget, setOfertasListTarget] = useState(null);
  const [creditOffersTarget, setCreditOffersTarget] = useState(null);

  // Selección de mora/GAC por cuota desde la tabla: { [quotaUuid]: { gac: bool, mora: bool } }.
  const [moraGacSelection, setMoraGacSelection] = useState({});

  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState();
  const [texts, setTexts] = useState("");

  useEffect(() => {
    if (localStorage.getItem("userUuid") == null) {
      window.location = "/admin";
    }
  }, []);

  useEffect(() => {
    if (textClient.trim() === "") {
      setDataClient([]);
      return;
    }
    // Buscador del módulo: por documento/nombre o por el ID de un crédito. Devuelve cada cliente con sus
    // IDs de crédito activos, para distinguir homónimos y ubicar al cliente por el ID del crédito.
    axiosClient
      .get(`/v1/condonations/clients/search`, {
        params: { query: textClient },
      })
      .then((response) => {
        setDataClient(response.data || []);
      })
      .catch((error) => {
        console.log("Error: " + error.message);
      });
  }, [textClient]);

  // IDs (oldId) de crédito del cliente, como texto para mostrar en la lista.
  const creditIdsLabel = (option) =>
    (option?.credits || [])
      .map((credit) => credit?.oldId)
      .filter((oldId) => oldId != null)
      .join(", ");

  const getOptionLabel = (option) => {
    if (typeof option === "string") {
      return option;
    }
    return (
      option?.fullName ||
      `${option?.name || ""} ${option?.lastName || ""}`.trim() ||
      String(option?.documentNumber || "")
    );
  };

  const showFeedback = (feedbackSeverity, message) => {
    setSeverity(feedbackSeverity);
    setTexts(message);
    setOpenSnackbar(true);
  };

  const fetchCredits = (clientUuid) => {
    setOpenBackdrop(true);
    setSelectedCredit(null);
    setQuotas(null);
    axiosClient
      .get(`/v1/condonations/clients/${clientUuid}/credits`)
      .then((response) => {
        setOpenBackdrop(false);
        setCredits(response.data || []);
      })
      .catch((error) => {
        setOpenBackdrop(false);
        showFeedback("error", "No se pudieron consultar los créditos del cliente.");
        console.log("Error: " + error.message);
      });
  };

  const handleSearch = () => {
    const normalizedSearch = textClient.trim().toLowerCase();

    const clientToSearch =
      selectedClient?.uuid
        ? selectedClient
        : dataClient.find((item) => {
            const fullName = item?.fullName?.toLowerCase() || "";
            const name = item?.name?.toLowerCase() || "";
            const lastName = item?.lastName?.toLowerCase() || "";
            const documentNumber = String(item?.documentNumber || "").toLowerCase();
            // También empareja por el ID de un crédito del cliente, para buscar por ID de crédito.
            const matchesCreditId = (item?.credits || []).some(
              (credit) => String(credit?.oldId ?? "").toLowerCase() === normalizedSearch
            );
            return (
              item?.uuid &&
              (fullName === normalizedSearch ||
                name === normalizedSearch ||
                lastName === normalizedSearch ||
                documentNumber === normalizedSearch ||
                matchesCreditId)
            );
          });

    if (!clientToSearch?.uuid) {
      Swal.fire({
        icon: "warning",
        title: "Cliente no válido",
        text: "Selecciona un cliente de la lista antes de buscar.",
        confirmButtonColor: "#161e55",
      });
      return;
    }
    setSelectedClient(clientToSearch);
    setSearchedClient(clientToSearch);
    fetchCredits(clientToSearch.uuid);
  };

  const fetchQuotas = (creditUuid) => {
    setOpenBackdrop(true);
    setMoraGacSelection({});
    axiosClient
      .get(`/v1/condonations/credits/${creditUuid}/quotas/all`)
      .then((response) => {
        setOpenBackdrop(false);
        setQuotas(response.data || []);
      })
      .catch((error) => {
        setOpenBackdrop(false);
        showFeedback("error", "No se pudieron consultar las cuotas del crédito.");
        console.log("Error: " + error.message);
      });
  };

  const handleSelectCredit = (credit) => {
    setSelectedCredit(credit);
    fetchQuotas(credit.uuid);
  };

  const refreshSelectedCredit = () => {
    if (selectedCredit) {
      fetchQuotas(selectedCredit.uuid);
    }
  };

  const handleActionSuccess = (message) => {
    showFeedback("success", message);
    refreshSelectedCredit();
  };

  const clientFullName =
    searchedClient?.fullName ||
    `${searchedClient?.name || ""} ${searchedClient?.lastName || ""}`.trim();

  // Única cuota condonable del crédito (cierra el crédito). Alimenta el botón "Condonar el total del crédito".
  const condonableQuota = Array.isArray(quotas) ? quotas.find((item) => item?.condonable) : null;

  // Cuotas VENCIDAS del crédito (solo sobre lo vencido se puede condonar). Alimentan la suma de saldos.
  const overdueQuotas = Array.isArray(quotas) ? quotas.filter(isActiveQuotaOverdue) : [];
  const overdueSaldoTotal = roundMoney(
    overdueQuotas.reduce((sum, item) => sum + Number(item?.pending?.totalPending || 0), 0)
  );

  // Cuotas cuyo GAC/mora se puede condonar (vencidas con pendiente de ese componente).
  const quotasWithGac = overdueQuotas.filter(canCondoneGac);
  const quotasWithMora = overdueQuotas.filter(canCondoneMora);

  const isGacSelected = (activeQuota) => !!moraGacSelection[activeQuota.quota.uuid]?.gac;
  const isMoraSelected = (activeQuota) => !!moraGacSelection[activeQuota.quota.uuid]?.mora;

  const toggleComponentSelection = (activeQuota, component, checked) => {
    const quotaUuid = activeQuota.quota.uuid;
    setMoraGacSelection((prev) => {
      const current = prev[quotaUuid] || { gac: false, mora: false };
      return { ...prev, [quotaUuid]: { ...current, [component]: checked } };
    });
  };

  const toggleAllComponent = (eligibleQuotas, component, checked) => {
    setMoraGacSelection((prev) => {
      const next = { ...prev };
      eligibleQuotas.forEach((activeQuota) => {
        const quotaUuid = activeQuota.quota.uuid;
        const current = next[quotaUuid] || { gac: false, mora: false };
        next[quotaUuid] = { ...current, [component]: checked };
      });
      return next;
    });
  };

  // Selección efectiva (solo componentes que siguen siendo condonables) que alimenta el botón y el diálogo.
  const moraGacSelectionList = overdueQuotas
    .map((activeQuota) => ({
      activeQuota,
      gac: canCondoneGac(activeQuota) && isGacSelected(activeQuota),
      mora: canCondoneMora(activeQuota) && isMoraSelected(activeQuota),
    }))
    .filter((item) => item.gac || item.mora);
  const hasMoraGacSelection = moraGacSelectionList.length > 0;

  const gacAllSelected =
    quotasWithGac.length > 0 && quotasWithGac.every((activeQuota) => isGacSelected(activeQuota));
  const moraAllSelected =
    quotasWithMora.length > 0 && quotasWithMora.every((activeQuota) => isMoraSelected(activeQuota));

  return (
    <ThemeProvider theme={pramiCertificadosTheme}>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "100%",
          minWidth: 0,
          bgcolor: PRAMI_COLORS.pageBg,
          boxSizing: "border-box",
          px: { xs: 2, sm: 3 },
          pb: 3,
        }}
      >
        <CssBaseline />
        <Toolbar sx={{ minHeight: { xs: "96px !important", sm: "104px !important" } }} />

        <Box
          sx={{
            width: "100%",
            maxWidth: 1200,
            mx: "auto",
            mt: 2,
          }}
        >
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12}>
              <Item>
                <SectionTitle variant="h6" sx={{ mb: 0.5 }}>
                  Condonaciones
                </SectionTitle>
                <Typography variant="body2" color="text.secondary">
                  Busca un cliente, elige el crédito por su ID y condona cuotas o el saldo completo.
                </Typography>
              </Item>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: { xs: 2, sm: 2.5 }, ...surfacePaperSx }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm>
                    <Autocomplete
                      id="condonaciones-client-search"
                      freeSolo
                      options={dataClient}
                      getOptionLabel={getOptionLabel}
                      filterOptions={(options) => options}
                      onInputChange={(event, value) => setTextClient(value || "")}
                      onChange={(event, value) => setSelectedClient(typeof value === "string" ? null : value)}
                      fullWidth
                      renderOption={(props, option) => {
                        const creditIds = creditIdsLabel(option);
                        return (
                          <li {...props} key={option?.uuid || getOptionLabel(option)}>
                            <Box sx={{ display: "flex", flexDirection: "column", py: 0.25 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {getOptionLabel(option)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {`Documento: ${option?.documentNumber || "—"}`}
                                {creditIds ? ` · Créditos: ${creditIds}` : ""}
                              </Typography>
                            </Box>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Buscar cliente" sx={squareFieldSx} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm="auto">
                    <ButtonPrincipal
                      variant="contained"
                      fullWidth
                      onClick={handleSearch}
                      startIcon={<SearchIcon />}
                      sx={{ minHeight: 56, px: 3, whiteSpace: "nowrap" }}
                    >
                      Buscar
                    </ButtonPrincipal>
                  </Grid>
                </Grid>

                {searchedClient ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ mt: 2 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Cliente{" "}
                      <strong style={{ color: PRAMI_COLORS.primary }}>
                        {clientFullName || "encontrado"}
                      </strong>
                    </Typography>
                    {searchedClient.documentNumber ? (
                      <Chip
                        label={`Documento: ${searchedClient.documentNumber}`}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: PRAMI_COLORS.surfaceBorder }}
                      />
                    ) : null}
                  </Stack>
                ) : null}
              </Paper>
            </Grid>

          {credits && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, ...surfacePaperSx }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ mb: 1 }}
                >
                  <SectionTitle variant="subtitle1">Créditos activos</SectionTitle>
                  <Typography variant="caption" color="text.secondary">
                    Usa Ver cuotas para consultar el detalle del crédito.
                  </Typography>
                </Stack>

                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: PRAMI_COLORS.primary }}>
                        <TableCell sx={headerCellSx} />
                        <TableCell sx={headerCellSx}>ID crédito</TableCell>
                        <TableCell sx={headerCellSx}>Cliente</TableCell>
                        <TableCell sx={headerCellSx}>Documento</TableCell>
                        <TableCell sx={headerCellSx}>Saldo total</TableCell>
                        <TableCell sx={headerCellSx}>Saldo capital</TableCell>
                        <TableCell sx={headerCellSx}>Cuotas</TableCell>
                        <TableCell sx={headerCellSx}>Estado</TableCell>
                        <TableCell sx={headerCellSx}>Origen</TableCell>
                        <TableCell sx={headerCellSx} align="center">
                          Acciones
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {credits.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No hay créditos activos para este cliente.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {credits.map((credit) => {
                        const selected = selectedCredit?.uuid === credit.uuid;
                        return (
                          <TableRow
                            key={credit.uuid}
                            hover
                            selected={selected}
                            onClick={() => handleSelectCredit(credit)}
                            sx={selectedRowSx}
                          >
                            <TableCell padding="checkbox">
                              <Radio
                                checked={selected}
                                onChange={() => handleSelectCredit(credit)}
                                value={credit.uuid}
                                sx={{
                                  color: PRAMI_COLORS.primary,
                                  "&.Mui-checked": { color: PRAMI_COLORS.primary },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{formatCreditOldId(credit)}</TableCell>
                            <TableCell>
                              {`${credit.client?.name || ""} ${credit.client?.lastName || ""}`.trim() ||
                                "-"}
                            </TableCell>
                            <TableCell>{credit.client?.documentNumber || "-"}</TableCell>
                            <TableCell>{formatMoney(credit.balanceCredit)}</TableCell>
                            <TableCell>{formatMoney(credit.saldoCapital)}</TableCell>
                            <TableCell>{credit.quotasNumber}</TableCell>
                            <TableCell>{statusChip(credit.status, CREDIT_STATUS_LABELS)}</TableCell>
                            <TableCell>{formatDate(credit.originDate)}</TableCell>
                            <TableCell
                              align="center"
                              onClick={(event) => event.stopPropagation()}
                              sx={{ minWidth: 240 }}
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="center"
                                flexWrap="wrap"
                                sx={{ gap: 1 }}
                              >
                                <ButtonPrincipal
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleSelectCredit(credit)}
                                  sx={{ textTransform: "none" }}
                                >
                                  Ver cuotas
                                </ButtonPrincipal>
                                <ButtonSecondary
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setCreditOffersTarget(credit)}
                                  sx={{ textTransform: "none" }}
                                >
                                  Ver ofertas
                                </ButtonSecondary>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}

          {selectedCredit && quotas && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, ...surfacePaperSx }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ mb: 1 }}
                >
                  <SectionTitle variant="subtitle1">
                    {`Cuotas del crédito ${formatCreditOldId(selectedCredit)}`}
                  </SectionTitle>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Box sx={{ textAlign: "right", mr: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        Saldo vencido (suma de cuotas vencidas)
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRAMI_COLORS.primary }}>
                        {formatMoney(overdueSaldoTotal)}
                      </Typography>
                    </Box>
                    {condonableQuota && (
                      <ButtonPrincipal
                        size="small"
                        variant="contained"
                        onClick={() => setCondonarTarget(condonableQuota)}
                        sx={{ textTransform: "none" }}
                      >
                        Condonar el total del crédito
                      </ButtonPrincipal>
                    )}
                    <ButtonPrincipal
                      size="small"
                      variant="contained"
                      onClick={() => setOfertaCreditoTarget(selectedCredit)}
                      sx={{ textTransform: "none" }}
                    >
                      Condonación sobre saldo total
                    </ButtonPrincipal>
                    {hasMoraGacSelection && (
                      <ButtonPrincipal
                        size="small"
                        variant="contained"
                        onClick={() => setMoraGacTarget(selectedCredit)}
                        sx={{
                          textTransform: "none",
                          bgcolor: PRAMI_COLORS.secondaryGreen,
                          "&:hover": { bgcolor: "#25692a" },
                        }}
                      >
                        {`Condonar mora y/o GAC (${moraGacSelectionList.length})`}
                      </ButtonPrincipal>
                    )}
                  </Stack>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: PRAMI_COLORS.primary }}>
                        <TableCell sx={headerCellSx}>Cuota</TableCell>
                        <TableCell sx={headerCellSx}>Fecha de pago</TableCell>
                        <TableCell sx={headerCellSx}>Valor</TableCell>
                        <TableCell sx={headerCellSx}>Estado</TableCell>
                        <TableCell sx={headerCellSx}>Saldo cuota</TableCell>
                        <TableCell sx={headerCellSx} align="center">
                          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                            <Checkbox
                              size="small"
                              disabled={quotasWithGac.length === 0}
                              checked={gacAllSelected}
                              indeterminate={
                                !gacAllSelected &&
                                quotasWithGac.some((activeQuota) => isGacSelected(activeQuota))
                              }
                              onChange={(event) =>
                                toggleAllComponent(quotasWithGac, "gac", event.target.checked)
                              }
                              sx={{ color: PRAMI_COLORS.primaryContrast, p: 0.5, "&.Mui-checked": { color: PRAMI_COLORS.primaryContrast }, "&.MuiCheckbox-indeterminate": { color: PRAMI_COLORS.primaryContrast } }}
                            />
                            <span>GAC</span>
                          </Stack>
                        </TableCell>
                        <TableCell sx={headerCellSx} align="center">
                          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                            <Checkbox
                              size="small"
                              disabled={quotasWithMora.length === 0}
                              checked={moraAllSelected}
                              indeterminate={
                                !moraAllSelected &&
                                quotasWithMora.some((activeQuota) => isMoraSelected(activeQuota))
                              }
                              onChange={(event) =>
                                toggleAllComponent(quotasWithMora, "mora", event.target.checked)
                              }
                              sx={{ color: PRAMI_COLORS.primaryContrast, p: 0.5, "&.Mui-checked": { color: PRAMI_COLORS.primaryContrast }, "&.MuiCheckbox-indeterminate": { color: PRAMI_COLORS.primaryContrast } }}
                            />
                            <span>Mora</span>
                          </Stack>
                        </TableCell>
                        <TableCell sx={headerCellSx} align="center">
                          Acciones
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quotas.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No hay cuotas para este crédito.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {quotas.map((activeQuota) => {
                        // Solo las cuotas VENCIDAS (PENDING + payDate <= hoy) admiten ofertar/ver ofertas.
                        const overdue = isActiveQuotaOverdue(activeQuota);
                        const hasActions = overdue;
                        return (
                          <TableRow key={activeQuota.quota.uuid} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{activeQuota.quota.quotaNumber}</TableCell>
                            <TableCell>{formatDate(activeQuota.quota.payDate)}</TableCell>
                            <TableCell>{formatMoney(activeQuota.quota.value)}</TableCell>
                            <TableCell>
                              {statusChip(activeQuota.quota.status, QUOTA_STATUS_LABELS)}
                            </TableCell>
                            <TableCell>{formatMoney(activeQuota.pending?.totalPending)}</TableCell>
                            <TableCell align="center">
                              {canCondoneGac(activeQuota) ? (
                                <Stack alignItems="center">
                                  <Checkbox
                                    size="small"
                                    checked={isGacSelected(activeQuota)}
                                    onChange={(event) =>
                                      toggleComponentSelection(activeQuota, "gac", event.target.checked)
                                    }
                                    sx={{
                                      color: PRAMI_COLORS.primary,
                                      "&.Mui-checked": { color: PRAMI_COLORS.primary },
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatMoney(activeQuota.pending?.pendingGac)}
                                  </Typography>
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {canCondoneMora(activeQuota) ? (
                                <Stack alignItems="center">
                                  <Checkbox
                                    size="small"
                                    checked={isMoraSelected(activeQuota)}
                                    onChange={(event) =>
                                      toggleComponentSelection(activeQuota, "mora", event.target.checked)
                                    }
                                    sx={{
                                      color: PRAMI_COLORS.primary,
                                      "&.Mui-checked": { color: PRAMI_COLORS.primary },
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatMoney(activeQuota.pending?.pendingMora)}
                                  </Typography>
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ minWidth: 320 }}>
                              {hasActions ? (
                                <Stack
                                  direction="row"
                                  flexWrap="wrap"
                                  justifyContent="center"
                                  alignItems="center"
                                  sx={{ gap: 1, py: 0.5 }}
                                >
                                  {overdue && (
                                    <>
                                      <ButtonSecondary
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setOfertaTarget(activeQuota)}
                                      >
                                        Oferta
                                      </ButtonSecondary>
                                      <ButtonSecondary
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setOfertasListTarget(activeQuota)}
                                      >
                                        Ver ofertas
                                      </ButtonSecondary>
                                    </>
                                  )}
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
          </Grid>
        </Box>
      </Box>

      <CondonarQuotaDialog
        open={!!condonarTarget}
        activeQuota={condonarTarget}
        onClose={() => setCondonarTarget(null)}
        onSuccess={handleActionSuccess}
      />
      <OfertaDialog
        open={!!ofertaTarget}
        activeQuota={ofertaTarget}
        onClose={() => setOfertaTarget(null)}
        onSuccess={handleActionSuccess}
      />
      <OfertaCreditoDialog
        open={!!ofertaCreditoTarget}
        credit={ofertaCreditoTarget}
        onClose={() => setOfertaCreditoTarget(null)}
        onSuccess={handleActionSuccess}
      />
      <CondonarMoraGacDialog
        open={!!moraGacTarget}
        credit={moraGacTarget}
        selection={moraGacSelectionList}
        overdueQuotas={overdueQuotas}
        onClose={() => setMoraGacTarget(null)}
        onSuccess={handleActionSuccess}
      />
      <OfertasListDialog
        open={!!ofertasListTarget}
        activeQuota={ofertasListTarget}
        onClose={() => setOfertasListTarget(null)}
      />
      <CreditOffersDialog
        open={!!creditOffersTarget}
        credit={creditOffersTarget}
        onClose={() => setCreditOffersTarget(null)}
        onChanged={refreshSelectedCredit}
      />

      <Backdrop
        sx={{
          color: PRAMI_COLORS.primaryContrast,
          bgcolor: "rgba(22, 30, 84, 0.45)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={openBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert sx={{ width: "100%" }} severity={severity} onClose={() => setOpenSnackbar(false)}>
          {texts}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
